<template>
  <Scroller>
    <!-- Static sections -->
    <transition name="slide-fade" mode="out-in">
      <ScrollerSection id="intro" sticky="center" key="intro" v-if="!active">
        <div>
          <h1 class="f-title-5">
            How is Russia's invasion of Ukraine reshaping the energy world?
          </h1>
          <ScrollIcon>Keep scrolling to read more</ScrollIcon>
        </div>
      </ScrollerSection>
      <ScrollerSection
        id="pipeline"
        sticky="left"
        behind
        key="pipeline"
        v-if="['preInvasion', 'postInvasion'].includes(active)"
      >
        <div class="chart__container">
          <p class="f-ui-2-bold">
            Natural gas pipeline flows from Russia to the European Union and
            Türkiye
          </p>
          <span class="m-chart__units">mcm/day</span>
          <StagedChart
            class="pipelineChart"
            :baseOptions="pipelineDefault"
            :stages="pipelineStages"
            :stage="active === 'preInvasion' ? 0 : 1"
          />
          <StaticLegendTest
            spread
            :items="[
              {
                items: [
                  { label: 'Nord Stream', color: 'historic' },
                  { label: 'Ukraine', color: 'teal' },
                  { label: 'Turkstream', color: 'blue' },
                  { label: 'Poland', color: 'green' },
                  { label: 'Other', color: 'yellow' },
                ],
              },
            ]"
          />
        </div>
      </ScrollerSection>
      <ScrollerSection
        id="co2"
        sticky="right"
        behind
        key="co2"
        v-if="['co22021', 'co22022'].includes(active)"
      >
        <div class="chart__container">
          <span class="m-chart__units">Mt CO2</span>
          <StagedChart
            class="co2Chart"
            :baseOptions="co2Default"
            :stages="co2Stages"
            :stage="active === 'co22021' ? 0 : 1"
          />
          <StaticLegendTest
            spread
            :items="[
              {
                items: [
                  { label: 'Annual change:' },
                  { label: 'CO2 emissions', color: 'blue' },
                  { label: 'GDP', color: 'red' },
                ],
              },
            ]"
          />
        </div>
      </ScrollerSection>
    </transition>
    <transition name="bounce" mode="out-in">
      <ScrollerSection
        v-if="active === 'markets'"
        id="yellowCircles"
        key="yellowCircles"
        sticky="center"
        behind
      >
        <img
          src="https://iea.imgix.net/b4eb1fd5-f956-483c-b4a3-6187ee0607c8/circle_yellow.svg"
        />
      </ScrollerSection>
      <ScrollerSection
        v-if="active === 'policy'"
        id="redCircles"
        key="redCircles"
        sticky="right"
        behind
      >
        <img
          src="https://iea.imgix.net/b30950ee-5252-436f-8c02-d44e2e69f54b/circle_red.svg"
          width="800"
        />
      </ScrollerSection>
      <ScrollerSection
        v-if="active === 'trade'"
        key="blueCircles"
        id="blueCircles"
        sticky="left"
        behind
      >
        <img
          src="https://iea.imgix.net/11a7e469-8234-46d3-bba2-cffaf05537d4/circle_blue.svg"
        />
      </ScrollerSection>
      <ScrollerSection
        v-if="active === 'impact'"
        id="purpleCircles"
        key="purpleCircles"
        sticky="right"
        behind
      >
        <img
          src="https://iea.imgix.net/86e91c49-bbf8-47de-96e2-920f8a857c45/circle_purple.svg"
        />
      </ScrollerSection>
    </transition>
    <!-- Normal sections -->
    <ScrollerSection
      id="markets"
      @enter="handleEnter"
      @leave="handleLeave"
      class="noBG"
    >
      <Block title="Energy markets">
        <p>
          High and volatile energy prices are hurting households and businesses,
          shifting the choice of fuels and setting back progress towards
          achieving universal access to energy.
        </p>
      </Block>
    </ScrollerSection>
    <ScrollerSection id="policy" @enter="handleEnter" class="noBG">
      <LeftBlock title="Energy policy">
        <p>
          Short-term responses have focused on securing available supply and
          protecting consumers, but many governments in the US, EU and elsewhere
          have adopted new policies that give a major boost to investments in
          clean energy and efficiency.
        </p>
      </LeftBlock>
    </ScrollerSection>
    <ScrollerSection id="trade" @enter="handleEnter" class="noBG">
      <Block>
        <h4 class="f-title-5">Energy trade</h4>
        <p>
          European sanctions on coal and oil imports and Gazprom's decisions to
          cut gas supply are triggering a profound reshuffling of trade flows
          around the world.
        </p>
      </Block>
    </ScrollerSection>
    <ScrollerSection id="impact" @enter="handleEnter" class="noBG">
      <LeftBlock title="Economic impacts">
        <p>
          High fossil fuel prices are stoking inflationary pressures; the
          combination of falling real incomes and rising prices is creating a
          looming risk of global recession.
        </p>
      </LeftBlock>
    </ScrollerSection>
    <ScrollerSection id="preInvasion" @enter="handleEnter">
      <Block>
        <h4 class="f-title-5">
          Russia has cut its natural gas pipeline flows...
        </h4>
      </Block>
    </ScrollerSection>
    <ScrollerSection id="postInvasion" @enter="handleEnter">
      <Block>
        <p>...to the European Union by around 80% since invading Ukraine.</p>
      </Block>
    </ScrollerSection>
    <ScrollerSection id="co22021" @enter="handleEnter">
      <LeftBlock
        title="CO2 emissions from global fossil fuel combustion saw a record rise in 2021..."
      />
    </ScrollerSection>
    <ScrollerSection id="co22022" @enter="handleEnter">
      <LeftBlock>
        <p>
          ...but a strong expansion of renewables and electric vehicles is
          expected to dampen growth in 2022.
        </p>
      </LeftBlock>
    </ScrollerSection>
    <ScrollerSection id="fin" @enter="handleEnter" @leave="handleLeave" />
  </Scroller>
</template>

<script>
import {
  Scroller,
  ScrollerSection,
  ScrollIcon,
  Block,
} from "@international-energy-agency/vue-ui";
import StagedChart from "../components/StagedChart.vue";
import StaticLegendTest from "../components/StaticLegendTest.vue";
import LeftBlock from "../components/LeftBlock.vue";
import ActiveMixin from "../mixins/active";
import pipelineStages from "../data/pipeline";
export default {
  components: {
    StagedChart,
    StaticLegendTest,
    ScrollerSection,
    ScrollIcon,
    Scroller,
    Block,
    LeftBlock,
  },
  mixins: [ActiveMixin],
  data() {
    return {
      co2Default: {
        chart: {
          type: "column",
        },
        legend: {
          enabled: false,
        },
        tooltip: {
          enabled: false,
        },
        exporting: {
          enabled: false,
        },
        xAxis: {
          min: 2020,
          max: 2022,
        },
        plotOptions: {
          series: {
            states: {
              hover: {
                enabled: false,
              },
            },
          },
        },
        yAxis: [
          {
            tickAmount: 5,
            min: -2000,
            max: 2000,
            title: {
              text: null,
            },
          },
          {
            tickAmount: 5,
            min: -6,
            max: 6,
            opposite: true,
            labels: {
              format: "{value}%",
            },
            title: {
              text: null,
            },
            plotLines: [
              {
                value: 0,
                className: "highcharts-zero-axis",
                zIndex: 99,
              },
            ],
          },
        ],
      },
      co2Stages: [
        {
          series: [
            {
              name: "CO2 emissions",
              className: "color-blue",
              data: [
                [2020, -1847],
                [2021, 1786],
              ],
            },
            {
              name: "GDP growth",
              className: "color-red highcharts-dotted",
              type: "line",
              marker: {
                enabled: true,
              },
              yAxis: 1,
              data: [
                [2020, -3.2],
                [2021, 5.9],
              ],
            },
          ],
        },
        {
          series: [
            {
              name: "CO2 emissions",
              className: "color-blue",
              data: [
                [2020, -1847],
                [2021, 1786],
                [2022, 296],
              ],
            },
            {
              name: "GDP growth",
              className: "color-red highcharts-dotted",
              type: "line",
              yAxis: 1,
              marker: {
                enabled: true,
                className: "color-red",
              },
              data: [
                [2020, -3.2],
                [2021, 5.9],
                [2022, 3.2],
              ],
            },
          ],
        },
      ],
      pipelineDefault: {
        chart: {
          type: "area",
          animation: {
            duration: 1,
          },
        },
        plotOptions: {
          series: {
            label: {
              enabled: false,
              connectorAllowed: true,
              onArea: false,
            },
            states: {
              hover: {
                enabled: false,
              },
            },
          },
        },
        tooltip: {
          enabled: false,
        },
        legend: {
          enabled: false,
        },
        exporting: {
          enabled: false,
        },
        yAxis: {
          max: 400,
          tickAmount: 5,
          plotLines: [
            { value: 0, className: "highcharts-zero-axis", zIndex: 99 },
          ],
        },
        xAxis: {
          type: "datetime",
          max: 1665360000000,
          tickPositions: [1641081600000, 1665360000000],
          labels: {
            format: "{value:%b %y}",
          },
          plotLines: [
            {
              value: 1645747200000,
              className: "highcharts-dotted",
              label: {
                text: "<b>Russia's invasion of Ukraine</b><br>Feb 24, 2022",
                rotation: 0,
              },
              zIndex: 99,
            },
          ],
        },
      },
      pipelineStages,
    };
  },
};
</script>

<style>
@import url("../css/main.css");
.noBG .scroller__content .m-block--text {
  background: none !important;
}
.pipelineChart .highcharts-area-series path {
  stroke-width: 0;
}
.highcharts-series-label text {
  fill: inherit !important;
}
.chart__container {
  width: 100%;
}
.highcharts-markers.color-red path.highcharts-point {
  fill: #d65a40;
}
</style>
